import { Checkbox, Dropdown, Flex, Grid, Header, Input, ProviderConsumer } from "@fluentui/react-northstar";
import { Communication } from '@fluentui/react-teams';
import React, { useCallback, useEffect, useReducer, useState } from "react";
import { Helmet } from 'react-helmet';
import { useTranslation } from "react-i18next";
import { checkInTeams } from "../App";
import { appState, useSetState } from '../AppState';
import { getCachedFeature, hasConsentFeature, setCachedFeature } from "../shared/cache/FeatureCache";
import { setCachedTicketSettings } from '../shared/cache/TicketSettingsCache';
import { getBodyHeight } from '../shared/common/CommonHelper';
import { Breadcrumbs } from "../shared/components/BreadcrumbNavigation";
import { CheckLicense } from "../shared/components/License/CheckLicense";
import { LoadingScreen } from "../shared/components/LoadingScreen";
import { Toolbar } from '../shared/components/Toolbar';
import { FeatureConsentNeeded, UpgradeNeeded } from "../shared/components/UpgradeNeeded";
import useUnsavedChangesWarning from "../shared/hooks/useUnsavedChangesWarning";
import { platformService } from '../shared/services/platform.service';
import { ADMINISTRATORS, ConfigurationName, CONTACT_ADMIN, DEFAULT_API_STATUS, DONE, ERROR, LICENSE_FEATURE, LOADING, SAVE, SETTINGS, SOMETHING_WENT_WRONG, UNAUTHORIZED_ACCESS } from "../shared/utils/constants";
import { toTitleCase } from "../shared/utils/helper";
import './config.css';

type TikitConfigurationForm = {
  logoUrl?: string;
}

export const TikitConfigState: TikitConfiguration = {
    IsResolutionNoteEnabled: false,
    RequireCategory: true,
    RequireNote: true,
    RequireInternalNote: false,
    ShowDraftKB:false,
    SurveysEnabled: false,
    GroupChatEnabled: false,
    EnableServiceCatalog: false,
    RequestBehavior: "Deflection",
    IsTimeTrackingEnabled: false,
    RestrictTimeEntryToCurrentDay: false,
    CaptureStartTime: false,
    RestrictTimeEntryToQuarterHours: false,
    AgentsCanEditTheirOwnComments: false,
    AgentsCanDeleteTheirOwnComments: false,
  };


export const TikitConfiguration = () => {
  const { t } = useTranslation();
  const api = new platformService();

  const [apiStatus, setApiStatus] = useState<APIStatus>(DEFAULT_API_STATUS as APIStatus);
  const [isResolutionNoteEnabled, setIsResolutionNoteEnabled] = useState(true);
  const [isSurveyEnabled, setIsSurveyEnabled] = useState(true);
  const [isGroupChatFeatureEnabled, setIsGroupChatFeatureEnabled] = useState(true);
  // Licence ONLY - deliberately not getCachedFeature, which folds in the tenant setting this
  // very toggle controls. Gating the switch on its own value would hide it once switched off.
  const [isTimeTrackingFeatureEnabled, setIsTimeTrackingFeatureEnabled] = useState(true);
  const [isScEnabled, setIsScEnabled] = useState(false);
  const [isGroupChatConsented, setIsGroupChatConsented] = useState(false);
  const [companyBrandingLogoUrl, setCompanyBrandingLogoUrl] = useState("");
  const [firstLoadData, setFirstLoadData] = useState<TikitConfigurationForm>({logoUrl: ""});

  const editPageReducer = (state, action) => {
    return {
      ...state,
      ...action.data
    };
  }

  const [state, dispatch] = useReducer(editPageReducer, TikitConfigState);
  // Snapshot of what the server gave us, for the dirty comparison below.
  const [originalState, setOriginalState] = useState<TikitConfiguration>(TikitConfigState);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [Prompt, setDirty] = useUnsavedChangesWarning();
  const setFormDirty: (isDirty: boolean) => void = setDirty as (isDirty: boolean) => void;
  const appContextState = appState();
  const setAppState = useSetState();

  useEffect(() => {
    fetchSystemSetting();
  }, []);

  // Every field is a primitive, so a stringify compare is enough - no deep clone needed.
  // This only holds while change() dispatches a patch instead of mutating state in place.
  useEffect(() => {
    const isDirty = JSON.stringify(state) !== JSON.stringify(originalState)
      || companyBrandingLogoUrl !== firstLoadData.logoUrl;
    setIsSaveDisabled(!isDirty);
    setFormDirty(isDirty);
  }, [state, originalState, companyBrandingLogoUrl, firstLoadData]);

  const fetchSystemSetting = useCallback(async () => {
    setApiStatus({ ...apiStatus, msg: t('resolution-note.fetching-system-setting') });
    try {
      const [resolutionNote, survey, ticket, groupChat, resolutonNoteFeature, surveyFeature, scFeature, groupChatFeatureEnable, timeTrackingFeatureEnable] = await Promise.all([
        api.getSystemSetting(ConfigurationName.Resolution),
        api.getSystemSetting(ConfigurationName.Survey),
        api.getSystemSetting(ConfigurationName.Ticket),
        api.getSystemSetting(ConfigurationName.GroupChat),
        getCachedFeature(LICENSE_FEATURE.ResolutionNote),
        getCachedFeature(LICENSE_FEATURE.Surveys),
        getCachedFeature(LICENSE_FEATURE.ServiceCatalog),
        api.groupChatFeatureEnabled(),
        api.hasFeature(LICENSE_FEATURE.TimeTracking)
      ]);

      const { data } = resolutionNote;
      setIsResolutionNoteEnabled(resolutonNoteFeature);
      setIsSurveyEnabled(surveyFeature);
      setIsScEnabled(scFeature);
      setIsGroupChatFeatureEnabled(groupChatFeatureEnable.data.value);
      setIsTimeTrackingFeatureEnabled(timeTrackingFeatureEnable.data.value);
      // Blocking on purpose. This resolves to CheckGroupChatConsent, which acquires an Azure AD
      // app token and then calls Microsoft Graph (CheckGroupChatConsent.cs:22-25) - ~800ms warm,
      // 13s+ once the token cache is cold, and slow in production too. Rendering the page before
      // it returns was tried and rejected: the Grant Consent warning gates the feature, and an
      // admin seeing the page without it would wrongly conclude Group Chat is working.
      setIsGroupChatConsented(await hasConsentFeature('GROUP_CHAT'));
      setCompanyBrandingLogoUrl(ticket.data.CompanyLogoUrl ?? "");
      setFirstLoadData({logoUrl: ticket.data.CompanyLogoUrl ?? ""});

      const loaded: TikitConfiguration = {
        ...TikitConfigState,
        IsResolutionNoteEnabled: data["IsEnabled"],
        RequireCategory: data["RequireCategory"],
        RequireNote: data["RequireNote"],
        RequireInternalNote: data["RequireInternalNote"],
        ShowDraftKB: data["ShowDraftKB"],
        SurveysEnabled: survey.data["IsEnabled"],
        GroupChatEnabled: groupChat.data["IsEnabled"],
        EnableServiceCatalog: ticket.data.EnableServiceCatalog,
        RequestBehavior: ticket.data.RequestBehavior,
        IsTimeTrackingEnabled: ticket.data.IsTimeTrackingEnabled ?? false,
        RestrictTimeEntryToCurrentDay: ticket.data.RestrictTimeEntryToCurrentDay ?? false,
        CaptureStartTime: ticket.data.CaptureStartTime ?? false,
        RestrictTimeEntryToQuarterHours: ticket.data.RestrictTimeEntryToQuarterHours ?? false,
        AgentsCanEditTheirOwnComments: ticket.data.AgentsCanEditTheirOwnComments ?? TikitConfigState.AgentsCanEditTheirOwnComments,
        AgentsCanDeleteTheirOwnComments: ticket.data.AgentsCanDeleteTheirOwnComments ?? TikitConfigState.AgentsCanDeleteTheirOwnComments
      };
      dispatch({ data: loaded });
      setOriginalState(loaded);

      // Deliberately inside the try - a genuine fetch failure must not be overwritten with DONE.
      const isAdmin = appContextState.userRoles.roles.includes(ADMINISTRATORS);
      setApiStatus(prev => (!isAdmin)
        ? { ...prev, status: ERROR, errCode: 401 }
        : { ...prev, status: DONE });
    } catch (err: any) {
      setApiStatus(prev => ({ ...prev, status: ERROR, errCode: err?.response?.status ?? 500 }));
    }
  }, []);

  const change = (e, x, propertyType) => {
    dispatch({ data: { [propertyType]: x.checked } });
  }

  const saveData = async () => {
    setApiStatus({ status: LOADING, msg: t('resolution-note.saving-settings'), errCode: 0 });
    try {
      await api.setSystemSetting(ConfigurationName.Resolution, {
        RequireCategory: state.RequireCategory,
        RequireNote: state.RequireNote,
        RequireInternalNote: state.RequireInternalNote,
        ShowDraftKB: state.ShowDraftKB,
        IsEnabled: state.IsResolutionNoteEnabled
      });

      await api.setSystemSetting(ConfigurationName.Survey, { IsEnabled: state.SurveysEnabled });
      await api.setSystemSetting(ConfigurationName.GroupChat, { IsEnabled: state.GroupChatEnabled });
      await api.setSystemSetting(ConfigurationName.Ticket, {
        EnableServiceCatalog: state.EnableServiceCatalog,
        RequestBehavior: state.RequestBehavior,
        CompanyLogoUrl: companyBrandingLogoUrl,
        IsTimeTrackingEnabled: state.IsTimeTrackingEnabled, 
        RestrictTimeEntryToCurrentDay: state.RestrictTimeEntryToCurrentDay, 
        CaptureStartTime: state.CaptureStartTime, 
        RestrictTimeEntryToQuarterHours: state.RestrictTimeEntryToQuarterHours,
        AgentsCanEditTheirOwnComments: state.AgentsCanEditTheirOwnComments,
        AgentsCanDeleteTheirOwnComments: state.AgentsCanDeleteTheirOwnComments,
      });
      setCachedFeature(LICENSE_FEATURE.GroupChat, state.GroupChatEnabled);
      // Same reason as group chat above. The published TimeTracking flag is licence AND
      // this toggle (GetAvailableFeatures folds them together), so both halves go in.
      // Without this the cached value is only re-read once its hour-long TTL lapses, and
      // until then the tab, the header total, the activity filter and the list column all
      // stay on the old answer - the admin flips the switch and nothing happens.
      setCachedFeature(LICENSE_FEATURE.TimeTracking, isTimeTrackingFeatureEnabled && state.IsTimeTrackingEnabled);
      // Same reason again, for the three time entry settings. Their cache exists to stop the
      // rule page fetching this once per editor and the Time Tracking tab once per ticket;
      // without this line the admin flips a toggle and both stay on the old answer until the
      // cache lapses - the start time token still listed, the pickers still unrestricted.
      setCachedTicketSettings({
        RestrictTimeEntryToCurrentDay: state.RestrictTimeEntryToCurrentDay,
        RestrictTimeEntryToQuarterHours: state.RestrictTimeEntryToQuarterHours,
        CaptureStartTime: state.CaptureStartTime
      });

      setFormDirty(false);
      setOriginalState({ ...state });
      setFirstLoadData({ logoUrl: companyBrandingLogoUrl });
      setApiStatus(prev => ({ ...prev, status: DONE }));

      // These three live in app state as `teamsConfig` (IRequestPageConfig), loaded once at
      // startup in App.tsx - the browser nav reads the logo straight off it. Pushing the saved
      // values back in updates the nav in place, which is what the old window.location.reload()
      // was really for. A reload also cost the admin the whole page - and their scroll position,
      // and the Saved state - on every logo edit, and did nothing at all inside Teams, where
      // there is no nav to refresh.
      setAppState(prev => ({
        ...prev,
        teamsConfig: {
          ...prev.teamsConfig,
          CompanyLogoUrl: companyBrandingLogoUrl,
          EnableServiceCatalog: state.EnableServiceCatalog,
          RequestBehavior: state.RequestBehavior
        }
      }));
    } catch (err: any) {
      console.log(err);
      setApiStatus(prev => ({ ...prev, status: ERROR, errCode: err?.response?.status ?? 500 }));
    }
  };

  const navs: Breadcrumbs = {
    breadcrumbs: [
      {
        title: toTitleCase(SETTINGS),
        link: '/settings'
      },
      {
        title: toTitleCase(t('resolution-note.ticket-config')),
        link: ''
      }
    ]
  };
  const items = [
    { key: "Deflection", label: t('service-offering.config.request-behavior.deflection'), header: t('service-offering.config.request-behavior.deflection') },
    { key: "CreateTicket", label: t('service-offering.config.request-behavior.create-ticket'), header: t('service-offering.config.request-behavior.create-ticket') },
    { key: "Hidden", label: t('service-offering.config.request-behavior.hidden'), header: t('service-offering.config.request-behavior.hidden') }
  ];

  const errConfig = {
    fields: { title: apiStatus.errCode === 401 ? UNAUTHORIZED_ACCESS : SOMETHING_WENT_WRONG, desc: CONTACT_ADMIN }
  };
  return (
    <>
      <Helmet><title>{t('resolution-note.resolution-note-tikit')}</title></Helmet>
      {apiStatus.status === LOADING ? (<LoadingScreen message={apiStatus.msg} />) :
       apiStatus.status === ERROR ? (<Communication {...errConfig} />) :
        <ProviderConsumer render={globalTheme => {
          // Groups are separated by a bare <hr/>, matching Bot Configuration (TeamsConfig.tsx:379).
          // Sections inside a group get whitespace and the header size step only - a rule at both
          // levels reads as noise.
          const sectionSpacing = { paddingTop: '1.375rem' };

          return (
          <>
            <Toolbar globalTheme={globalTheme} title={`${toTitleCase(SETTINGS)} > ${toTitleCase(t('resolution-note.ticket-config'))}`} breadcrumbs={navs} saveText={SAVE} saveCommand={saveData} disableSave={isSaveDisabled} />
            <Flex style={{ height: getBodyHeight(checkInTeams()) }}>
              {/* paddingTop 0 and a 10px rowGap match Bot Configuration (TeamsConfig.tsx:348).
                  Northstar Header already carries ~19px of its own vertical margin, so anything
                  more here stacks on top of it and the groups drift apart. */}
              <Grid columns="auto" styles={{ padding: '0 1.5rem 2rem', paddingTop: 0, justifyContent: 'flex-start', alignContent: 'start', rowGap: '10px', overflow: 'auto', width: '100%' }}>

                <div>
                  <Header content={t('ticket-config.group.tickets')} />

                  <div>
                    <Header as="h2" className="pb-2 defaultStyle" content={t('tikit-setting.edit-comments')} />
                    <p className="text-sm config-copy">{t('tikit-setting.sub-copy')}</p>
                    <div className="flex flex-col">
                      <Checkbox
                        className="toggle-label"
                        label={t('tikit-setting.agents-can-edit-their-own-comments')}
                        toggle
                        onChange={(e, x) => change(e, x, "AgentsCanEditTheirOwnComments")}
                        checked={state.AgentsCanEditTheirOwnComments}
                      />
                      <Checkbox
                        className="toggle-label"
                        label={t('tikit-setting.agents-can-delete-their-own-comments')}
                        toggle
                        onChange={(e, x) => change(e, x, "AgentsCanDeleteTheirOwnComments")}
                        checked={state.AgentsCanDeleteTheirOwnComments}
                      />
                    </div>
                  </div>

                  <div style={sectionSpacing}>
                    <Header as="h2" className="pb-2 defaultStyle" content={t('resolution-note.resolution-note-configuration')} />
                    {(!isResolutionNoteEnabled) && (<UpgradeNeeded breadcrumbs={navs} headerText={t('resolution-note.you-do-not-have-access')} subheaderText={t('resolution-note.please-upgrade-your-plan')} svgSize={125} justify="justify-start" />)}
                    <CheckLicense disable>
                      {isResolutionNoteEnabled && (
                        <ul>
                          <li>
                            <div><Checkbox className="toggle-label" style={{ marginBottom: '7px' }} label={t('resolution-note.require-resolution')} toggle onChange={(e, x) => { change(e, x, "IsResolutionNoteEnabled") }} checked={state.IsResolutionNoteEnabled} /></div>
                            <div style={{ marginLeft: '52px' }}>
                              <ul>
                                <li>
                                  <Checkbox className="toggle-label" label={t('resolution-note.resolution-category-required')} toggle onChange={(e, x) => { change(e, x, "RequireCategory") }} disabled={!state.IsResolutionNoteEnabled} checked={state.RequireCategory} />
                                  <div className="resolution-note-copy">{t('resolution-note.resolution-category-copy')}</div>
                                </li>
                                <li>
                                  <Checkbox className="toggle-label" label={t('resolution-note.resolution-note-required')} toggle onChange={(e, x) => { change(e, x, "RequireNote") }} disabled={!state.IsResolutionNoteEnabled} checked={state.RequireNote} />
                                  <div className="resolution-note-copy">{t('resolution-note.resolution-note-copy')}</div>
                                </li>
                                <li>
                                  <Checkbox className="toggle-label" label={t('resolution-note.resolution-note-internal-required')} toggle onChange={(e, x) => { change(e, x, "RequireInternalNote") }} disabled={!state.IsResolutionNoteEnabled} checked={state.RequireInternalNote} />
                                  <div className="resolution-note-copy">{t('resolution-note.resolution-note-internal-copy')}</div>
                                </li>
                                <li>
                                  <Checkbox className="toggle-label" label={t('resolution-note.draft-kb')} toggle onChange={(e, x) => { change(e, x, "ShowDraftKB") }} disabled={!state.IsResolutionNoteEnabled} checked={state.ShowDraftKB} />
                                  <div className="resolution-note-copy">{t('resolution-note.draft-kb-copy')}</div>
                                </li>
                              </ul>
                            </div>
                          </li>
                        </ul>
                      )}
                    </CheckLicense>
                  </div>

                  <div style={sectionSpacing}>
                    <Header as="h2" className="pb-2 defaultStyle" content={t('time-tracking.config.title')} />
                    {(!isTimeTrackingFeatureEnabled) && (<UpgradeNeeded breadcrumbs={navs} headerText={t('time-tracking.config.you-do-not-have-access')} subheaderText={t('time-tracking.config.please-upgrade-your-plan')} svgSize={125} justify="justify-start" />)}
                    {isTimeTrackingFeatureEnabled && (<>
                      <p className="text-sm config-copy">{t('time-tracking.config.sub-copy')}</p>
                      <ul>
                        <li>
                          <div><Checkbox className="toggle-label" style={{ marginBottom: '7px' }} label={t('time-tracking.config.enable')} toggle onChange={(e, x) => change(e, x, "IsTimeTrackingEnabled")} checked={state.IsTimeTrackingEnabled} /></div>
                          <div style={{ marginLeft: '52px' }}>
                            <ul>
                              <li>
                                <Checkbox className="toggle-label" label={t('time-tracking.config.restrict-to-today')} toggle onChange={(e, x) => { change(e, x, "RestrictTimeEntryToCurrentDay") }} disabled={!state.IsTimeTrackingEnabled} checked={state.RestrictTimeEntryToCurrentDay} />
                                <div className="resolution-note-copy">{t('time-tracking.config.restrict-to-today-copy')}</div>
                              </li>
                              <li>
                                <Checkbox className="toggle-label" label={t('time-tracking.config.capture-start-time')} toggle onChange={(e, x) => { change(e, x, "CaptureStartTime") }} disabled={!state.IsTimeTrackingEnabled} checked={state.CaptureStartTime} />
                                <div className="resolution-note-copy">{t('time-tracking.config.capture-start-time-copy')}</div>
                              </li>
                              <li>
                                {/* Governs the duration's minutes as well as the start time, so the two
                                    cannot disagree - an agent who cannot start at 2:07 should not be
                                    able to log 2h 07m either. Stays available with capture off, since
                                    the minutes half applies regardless. */}
                                <Checkbox className="toggle-label" label={t('time-tracking.config.quarter-hours')} toggle onChange={(e, x) => { change(e, x, "RestrictTimeEntryToQuarterHours") }} disabled={!state.IsTimeTrackingEnabled} checked={state.RestrictTimeEntryToQuarterHours} />
                                <div className="resolution-note-copy">{t('time-tracking.config.quarter-hours-copy')}</div>
                              </li>
                            </ul>
                          </div>
                        </li>
                      </ul>
                    </>)}
                  </div>

                  <div style={sectionSpacing}>
                    <Header as="h2" className="pb-2 defaultStyle" content={t('group-chat.group-chat-configuration')} />
                    {(!isGroupChatFeatureEnabled) && (<UpgradeNeeded breadcrumbs={navs} headerText={t('group-chat.you-do-not-have-access')} subheaderText={t('group-chat.please-upgrade-your-plan')} svgSize={125} justify="justify-start" />)}
                    <CheckLicense disable>
                      {isGroupChatFeatureEnabled && (<>
                        <p className="text-sm config-copy">{t('group-chat.config.sub-copy')}</p>
                        <div><Checkbox className="toggle-label" label={t('group-chat.enable-group-chat')} toggle onChange={(e,x)=>{change(e, x, "GroupChatEnabled")}} checked={state.GroupChatEnabled} /></div>
                      </>)}
                      {!isGroupChatConsented && (<FeatureConsentNeeded />)}
                    </CheckLicense>
                  </div>

                  <div style={sectionSpacing}>
                    <Header as="h2" className="pb-2 defaultStyle" content={t('surveys.survey-configuration')} />
                    {(!isSurveyEnabled) && (<UpgradeNeeded breadcrumbs={navs} headerText={t('surveys.you-do-not-have-access')} subheaderText={t('surveys.please-upgrade-your-plan')} svgSize={125} justify="justify-start" />)}
                    <CheckLicense disable>
                      {isSurveyEnabled && (<>
                        <p className="text-sm config-copy">{t('surveys.config.sub-copy')}</p>
                        <div><Checkbox className="toggle-label" label={t('surveys.enable-surveys')} toggle onChange={(e, x) => { change(e, x, "SurveysEnabled") }} checked={state.SurveysEnabled} /></div>
                      </>)}
                    </CheckLicense>
                  </div>
                </div>

                <hr />

                <div>
                  <Header content={t('ticket-config.group.request-portal')} />

                  <div>
                    <Header as="h2" className="pb-2 defaultStyle" content={t('service-offering.config.request-behavior.title')} />
                    <p className="text-sm config-copy" dangerouslySetInnerHTML={{ __html: t('service-offering.config.request-behavior.sub-copy', { color: globalTheme.siteVariables.colorScheme.brand.foregroundActive })}} />
                    <Dropdown className="max-w-flyout" style={{ background: globalTheme.siteVariables.colorScheme.default.background }} fluid inverted items={items} value={items.find(x => x.key == state.RequestBehavior)} onChange={(e, p) => { dispatch({ data: { RequestBehavior: p.value['key'] } }); }} />
                  </div>

                  <div style={sectionSpacing}>
                    <Header as="h2" className="pb-2 defaultStyle" content={t('requests.service-catalog')} />
                    {(!isScEnabled) && (<UpgradeNeeded breadcrumbs={navs} headerText={t('service-offering.you-do-not-have-access')} subheaderText={t('service-offering.please-upgrade-your-plan')} svgSize={125} justify="justify-start" />)}
                    <CheckLicense disable>
                      {isScEnabled && (<>
                        <p className="text-sm config-copy" dangerouslySetInnerHTML={{ __html: t('service-offering.config.sub-copy', { color: globalTheme.siteVariables.colorScheme.brand.foregroundActive })}} />
                        <Checkbox className="toggle-label" label={t('service-offering.config.enable')} toggle onChange={(e, x) => change(e, x, "EnableServiceCatalog")} checked={state.EnableServiceCatalog} />
                      </>)}
                    </CheckLicense>
                  </div>
                </div>

                <hr />

                <div>
                  <Header content={t('ticket-config.group.branding')} />

                  <div>
                    <Header as="h2" className="pb-2 defaultStyle" content={t('company-branding.menu-title')} />
                    <p className="config-copy">{t('company-branding.sub-title')}</p>
                    <div className="max-w-flyout">
                      <Input inverted fluid placeholder={t('company-branding.placeholder')} value={companyBrandingLogoUrl} onChange={(_e, props) => {
                        setCompanyBrandingLogoUrl(props.value);
                      }} />
                    </div>
                  </div>
                </div>

              </Grid>
            </Flex>
            {Prompt}
          </>
        );}} />
      }
    </>
  );
}
